import { ReactComponent as BookingsIcon } from '@/assets/icons/bookings.svg';
import {
  Button,
  Chip,
  HSpacer,
  MenuItem,
  Search,
  Text,
  Toolbar,
  VSpacer,
} from '@/components/DesignSystem';
import { Avatar } from "@/components/DesignSystem/Avatar/Avatar";
import { Filter, FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import ERPRetailerSelector from '@/components/shared/ListSelector/ERPRetailerSelector';
import { QueryKeys } from '@/constants/QueryKeys';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useSearch } from '@/hooks/useSearch';
import { OrderTransactionsApi } from '@/utilities/api/OrderTransactionsApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { DashboardEndpoint } from "@api/endpoints";
import { ApiRetailer } from '@api/interfaces';
import { ApiResolvedBooking } from '@api/interfaces/ApiOrderTransaction';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Container,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Popover,
  Stack,
  Table,
  TextField,
  useTheme,
} from '@mui/material';
import { OrderTransactionStatus, TransactionType, UserType } from '@shared/enums';
import { businessToday, formatMediumDate, getAccountName } from '@shared/utilities';
import i18next from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { BookingsTableBody, BookingsTableHeader } from './BookingsTable';
import { Routes } from "@/constants/Routes";
import { useDashboardNavigate } from "@/pages/CustomerDashboard/helpers";

const ITEMS_PER_PAGE = 20;

const formatOrderLabel = (label: string): string => {
  return label
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const Bookings = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { isInternal, retailer: userRetailer } = useAuthentication();
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingExternalIdFromParams = searchParams.get('bookingExternalId');
  const retailerIdFromParams = searchParams.get('retailerId');
  const navigateToPage = useDashboardNavigate();

  const [activeBookingId, setActiveBookingId] = useState<string | null>(
    bookingExternalIdFromParams,
  );
  const [selectedRetailer, setSelectedRetailer] = useState<ApiRetailer | undefined>();
  const [search, setSearch] = useState('');
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => {
    const initialSelections = new Map();
    initialSelections.set('status', new Set([OrderTransactionStatus.ACTIVE]));
    return initialSelections;
  });
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [viewingFilter, setViewingFilter] = useState<'all' | 'with-points-activity'>('all');
  const [viewingFilterAnchorEl, setViewingFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [pointsRangeAnchorEl, setPointsRangeAnchorEl] = useState<HTMLElement | null>(null);
  const [pointsRangeFilter, setPointsRangeFilter] = useState({
    remainingMin: '',
    remainingMax: '',
    redeemedMin: '',
    redeemedMax: '',
    reservedMin: '',
    reservedMax: '',
  });

  const [debouncedPointsRangeFilter, setDebouncedPointsRangeFilter] = useState(pointsRangeFilter);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPointsRangeFilter(pointsRangeFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [pointsRangeFilter]);

  const pointsRangeApiFilter = useMemo(() => {
    const filter: Record<string, any> = {};

    if (debouncedPointsRangeFilter.remainingMin || debouncedPointsRangeFilter.remainingMax) {
      filter.pointsRemaining = {};
      if (debouncedPointsRangeFilter.remainingMin) {
        filter.pointsRemaining.min = Number(debouncedPointsRangeFilter.remainingMin);
      }
      if (debouncedPointsRangeFilter.remainingMax) {
        filter.pointsRemaining.max = Number(debouncedPointsRangeFilter.remainingMax);
      }
    }

    if (debouncedPointsRangeFilter.redeemedMin || debouncedPointsRangeFilter.redeemedMax) {
      filter.pointsRedeemed = {};
      if (debouncedPointsRangeFilter.redeemedMin) {
        filter.pointsRedeemed.min = Number(debouncedPointsRangeFilter.redeemedMin);
      }
      if (debouncedPointsRangeFilter.redeemedMax) {
        filter.pointsRedeemed.max = Number(debouncedPointsRangeFilter.redeemedMax);
      }
    }

    if (debouncedPointsRangeFilter.reservedMin || debouncedPointsRangeFilter.reservedMax) {
      filter.pointsReserved = {};
      if (debouncedPointsRangeFilter.reservedMin) {
        filter.pointsReserved.min = Number(debouncedPointsRangeFilter.reservedMin);
      }
      if (debouncedPointsRangeFilter.reservedMax) {
        filter.pointsReserved.max = Number(debouncedPointsRangeFilter.reservedMax);
      }
    }

    return filter;
  }, [debouncedPointsRangeFilter]);

  const {
    search: customerSearch,
    debouncedSearch: debouncedCustomerSearch,
    setSearch: setCustomerSearch,
  } = useSearch();

  const { data: retailerFromParams } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, retailerIdFromParams],
    () => RetailerApi.get(retailerIdFromParams!),
    { enabled: !!retailerIdFromParams && isInternal && !!activeBookingId },
  );

  useEffect(() => {
    if (retailerFromParams && !selectedRetailer && !!activeBookingId) {
      setSelectedRetailer(retailerFromParams);
    }
  }, [retailerFromParams, selectedRetailer]);

  useEffect(() => {
    if (!isInternal && userRetailer && !selectedRetailer) {
      setSelectedRetailer(userRetailer);
    }
  }, [isInternal, userRetailer, selectedRetailer]);

  const {
    data: customersData,
    fetchNextPage: fetchNextCustomerPage,
    hasNextPage: hasNextCustomerPage,
    isFetching: areCustomersFetching,
  } = useInfiniteQuery(
    [QueryKeys.LIST_USERS, selectedRetailer?.id, debouncedCustomerSearch],
    async ({ pageParam = 0 }) => {
      if (!selectedRetailer?.id) {
        throw new Error('Retailer ID is required');
      }
      return UserApi.list({
        page: pageParam,
        retailerId: selectedRetailer.id,
        search: debouncedCustomerSearch,
        userType: [UserType.Farmer],
      });
    },
    {
      enabled: !!selectedRetailer?.id,
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );


  const selectedStatuses = useMemo(() =>
    Array.from(filterSelections.get('status') ?? []),
  [filterSelections]);

  const hasPointsRangeFilter = Object.values(pointsRangeFilter).some(val => val !== '');

  const {
    data: bookingsData,
    isFetching: areBookingsFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [
      QueryKeys.LIST_BOOKINGS,
      selectedRetailer?.id,
      search,
      selectedCustomerIds,
      pointsRangeApiFilter,
      viewingFilter,
      activeBookingId,
      selectedStatuses,
    ],
    ({ pageParam = 0 }) => {
      if (!selectedRetailer?.id) {
        throw new Error('Retailer ID is required');
      }
      return OrderTransactionsApi.listBookingsByRetailer({
        retailerId: selectedRetailer.id,
        limit: ITEMS_PER_PAGE,
        page: pageParam,
        onlyWithPointsActivity: viewingFilter === 'with-points-activity',
        search: search || activeBookingId || undefined,
        ...(selectedCustomerIds.length > 0 ? { farmerIds: selectedCustomerIds } : {}),
        ...pointsRangeApiFilter,
        selectedStatuses: selectedStatuses as DashboardEndpoint.ListBookings.BookingStatusFilter[],
      });
    },
    {
      enabled: !!selectedRetailer?.id,
      getNextPageParam: (lastPage) => {
        if (lastPage.page < lastPage.lastPage) {
          return lastPage.page + 1;
        }
        return undefined;
      },
    },
  );

  const transformedBookings = useMemo(() => {
    if (!bookingsData?.pages) {
      return [];
    }

    return bookingsData.pages.flatMap((page) => page.data.map((booking: ApiResolvedBooking) => {
      const uniqueOrders = Array.from(
        new Map(
          (booking.invoicedProducts ?? []).map((product) => [
            product.order.id,
            product.order,
          ]),
        ).values(),
      ).sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());

      const getRedeemedPoints = (bookingId: string, histories: typeof booking.pointHistories) => {
        return histories?.filter((history) => (
          [TransactionType.BOOKING_REDEMPTION, TransactionType.BOOKING_REFUND].includes(
            history.type,
          ) && history.orderTransactionProduct?.linkedBookingId === bookingId
        )).reduce((acc, history) => acc + history.amount, 0) ?? 0;
      };

      let status: 'ACTIVE' | 'EXPIRED' | 'CLOSED' = 'ACTIVE';
      if (booking.isClosed) {
        status = 'CLOSED';
      }
      else if (booking.expirationDate && booking.expirationDate < businessToday()) {
        status = 'EXPIRED';
      }

      return {
        id: booking.id,
        status,
        bookingId: booking.groupingId,
        accountName: booking.farmer ? getAccountName(booking.farmer) : t($ => $.common.na),
        customerErpId: booking.farmer?.externalId || t($ => $.common.na),
        pointsReserved: booking.pointsReserved,
        pointsRedeemed: booking.pointsRedeemed,
        pointsRemaining: booking.pointsRemaining,
        lastUpdated: formatMediumDate(booking.updatedAt, i18next),
        bookingCreated: formatMediumDate(booking.orderDate, i18next),
        bookingExpires: booking.expirationDate
          ? formatMediumDate(booking.expirationDate, i18next)
          : t($ => $.common.na),
        isClosed: booking.isClosed,
        expirationDate: booking.expirationDate,
        orders: uniqueOrders.map((order) => ({
          date: formatMediumDate(order.orderDate, i18next),
          id: order.id,
          label: order.orderSubtype
            ? formatOrderLabel(order.orderSubtype)
            : t($ => $.common.order.order),
          erpId: order.groupingId,
          pointsRedeemed: Math.abs(getRedeemedPoints(booking.id, order.pointHistories ?? [])),
        })),
        subwalletBalances: booking.subwalletBalances,
      };
    }));
  }, [bookingsData]);

  if (!!userRetailer && !userRetailer.hasBookings) {
    navigateToPage(Routes.HOMEPAGE);
    return null;
  }

  return (
    <Container maxWidth="lg">
      <Stack>
        <Stack alignItems="center" direction="row">
          <ERPRetailerSelector
            header={t($ => $.bookings.title)}
            logo="store"
            onSelect={(retailer) => {
              setSelectedRetailer(retailer);
              setSearch('');
              setCustomerSearch('');
              setSelectedCustomerIds([]);
              setViewingFilter('all');
              setPointsRangeFilter({
                remainingMin: '',
                remainingMax: '',
                redeemedMin: '',
                redeemedMax: '',
                reservedMin: '',
                reservedMax: '',
              });
              const initialSelections = new Map();
              initialSelections.set('status', new Set(['active']));
              setFilterSelections(initialSelections);
            }}
          />
          <HSpacer size="5" />
          <Button
            onClick={(e) => setViewingFilterAnchorEl(e.currentTarget)}
            square={true}
            sx={{ height: '32px' }}
            testID="viewing-filter-menu"
          >
            <Stack alignItems="center" direction="row">
              <Text category="label-medium">
                {viewingFilter === 'all'
                  ? t($ => $.bookings.filters.allBookings)
                  : t($ => $.bookings.filters.onlyPointsActivity)}
              </Text>
              <HSpacer size="3" />
              <ArrowDropDown sx={{ height: '16px', width: '16px' }} />
            </Stack>
          </Button>
          <Menu
            anchorEl={viewingFilterAnchorEl}
            onClose={() => setViewingFilterAnchorEl(null)}
            open={Boolean(viewingFilterAnchorEl)}
          >
            <MenuItem
              onClick={() => {
                setViewingFilter('all');
                setViewingFilterAnchorEl(null);
              }}
              selected={viewingFilter === 'all'}
              testID="viewing-all"
            >
              <ListItemText>{t($ => $.bookings.filters.allBookings)}</ListItemText>
              {viewingFilter === 'all' && (
                <ListItemIcon>
                  <Check color="primary" />
                </ListItemIcon>
              )}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setViewingFilter('with-points-activity');
                setViewingFilterAnchorEl(null);
              }}
              selected={viewingFilter === 'with-points-activity'}
              testID="viewing-with-points"
            >
              <ListItemText>{t($ => $.bookings.filters.onlyPointsActivity)}</ListItemText>
              {viewingFilter === 'with-points-activity' && (
                <ListItemIcon>
                  <Check color="primary" />
                </ListItemIcon>
              )}
            </MenuItem>
          </Menu>
        </Stack>
        <VSpacer size="6" />
        <Search
          onChangeText={setSearch}
          placeholder={t($ => $.common.search.searchById)}
          testID="search-bookings"
          value={search}
          width={350}
        />
        <VSpacer size="6" />
        {selectedRetailer && (
          <>
            <Stack direction="row" spacing={0}>
              <Toolbar
                filterSelections={filterSelections}
                filters={[
                  {
                    id: 'customer',
                    isChipLabelVerbose: true,
                    label: t($ => $.common.customer),
                    infiniteScrollOptions: {
                      fetchMoreItems: fetchNextCustomerPage,
                      hasMore: hasNextCustomerPage ?? true,
                      isLoading: areCustomersFetching,
                    },
                    onSearch: (search) => setCustomerSearch(search),
                    options: (
                      customersData?.pages?.flatMap(({ data }) => data) ?? []
                    ).map((customer) => ({
                      id: customer.id,
                      label: getAccountName(customer),
                    })) ?? [],
                    search: customerSearch,
                    selectionMethod: 'multi-select',
                  } as Filter,
                  {
                    id: 'status',
                    isChipLabelVerbose: true,
                    label: t($ => $.bookings.filters.status),
                    options: [
                      {
                        id: 'active',
                        label: t($ => $.common.status.active),
                        default: true,
                      },
                      {
                        id: 'expired',
                        label: t($ => $.common.status.expired),
                      },
                      {
                        id: 'closed',
                        label: t($ => $.common.status.closed),
                      },
                    ],
                    selectionMethod: 'multi-select',
                  } as Filter,
                ]}
                hideSearch
                onChange={({ selections }) => {
                  setFilterSelections(new Map(selections));
                  const selectedCustomers = Array.from(selections?.get('customer') ?? []);
                  setSelectedCustomerIds(selectedCustomers);
                }}
                testID="bookings-filters"
                totalItems={bookingsData?.pages?.[0]?.total}
                totalUnitLabel={{
                  plural: t($ => $.common.units.bookings),
                  singular: t($ => $.common.units.booking),
                }}
              />
              <Chip
                label={t($ => $.bookings.filters.pointsRange)}
                onClick={(e) => setPointsRangeAnchorEl(e.currentTarget)}
                size="medium"
                sx={{
                  borderColor: '#bdbdbd',
                  fontWeight: 500,
                  ...(hasPointsRangeFilter ? {
                    backgroundColor: (theme) => theme.palette.primaryContainer,
                  } : {}),
                }}
                testID="points-range-filter-chip"
                variant={hasPointsRangeFilter ? 'filled' : 'outlined'}
              />
            </Stack>
          </>
        )}
        {isInternal && !selectedRetailer && (
          <Text category="body-medium" color="secondary">
            {t($ => $.bookings.selectRetailer)}
          </Text>
        )}
        {selectedRetailer && areBookingsFetching && transformedBookings.length === 0 && (
          <Stack alignItems="center" py="48px">
            <CircularProgress data-testid="bookings-loading" />
          </Stack>
        )}
        {selectedRetailer
          && !isFetchingNextPage
          && !areBookingsFetching
          && transformedBookings.length === 0
          && (
            <Stack alignItems="center" data-testid="bookings-empty" py="48px">
              <Avatar
                alt="reserved-point-card-logo"
                size={48}
                sx={{ color: theme.palette.primary.main }}
              >
                <BookingsIcon />
              </Avatar>
              <VSpacer size="3" />
              <Text category="body-medium">
                {t($ => $.bookings.emptyState)}
              </Text>
            </Stack>
          )
        }
        {selectedRetailer && transformedBookings.length > 0 && (
          <InfiniteScroll
            fetchMoreItems={() => fetchNextPage()}
            hasMore={hasNextPage ?? false}
            height="calc(100vh - 300px)"
            isLoading={areBookingsFetching || isFetchingNextPage}
          >
            <Table>
              <BookingsTableHeader />
              <BookingsTableBody bookings={transformedBookings} />
            </Table>
            {bookingExternalIdFromParams && (
              <>
                <VSpacer size="6" />
                <Stack alignItems="center">
                  <Button
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('bookingId');
                      setSearchParams(newParams);
                      setSearch('');
                      setActiveBookingId(null);
                    }}
                    testID="show-all-bookings-button"
                  >
                    {t($ => $.bookings.showAll)}
                  </Button>
                </Stack>
              </>
            )}
          </InfiniteScroll>
        )}
      </Stack>

      <Popover
        BackdropProps={{ invisible: true }}
        anchorEl={pointsRangeAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={() => setPointsRangeAnchorEl(null)}
        open={Boolean(pointsRangeAnchorEl)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 3, minWidth: 400 }}>
          <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
            <Text category="headline-small">{t($ => $.bookings.filters.rangeTitle)}</Text>
            <IconButton onClick={() => setPointsRangeAnchorEl(null)}>
              <Close />
            </IconButton>
          </Stack>
          <Stack spacing={3}>
            <Stack spacing={2}>
              <Text category="title-medium">{t($ => $.bookings.filters.pointsRemaining)}</Text>
              <Stack direction="row" spacing={2}>
                <TextField
                  data-testid="points-remaining-min"
                  label={t($ => $.common.filter.min)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPointsRangeFilter(prev => ({
                      ...prev,
                      remainingMin: e.target.value,
                    }))
                  }
                  type="number"
                  value={pointsRangeFilter.remainingMin}
                />
                <TextField
                  data-testid="points-remaining-max"
                  label={t($ => $.common.filter.max)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPointsRangeFilter(prev => ({
                      ...prev,
                      remainingMax: e.target.value,
                    }))
                  }
                  type="number"
                  value={pointsRangeFilter.remainingMax}
                />
              </Stack>
            </Stack>

            <Stack spacing={2}>
              <Text category="title-medium">{t($ => $.bookings.filters.pointsRedeemed)}</Text>
              <Stack direction="row" spacing={2}>
                <TextField
                  data-testid="points-redeemed-min"
                  label={t($ => $.common.filter.min)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPointsRangeFilter(prev => ({
                      ...prev,
                      redeemedMin: e.target.value,
                    }))
                  }
                  type="number"
                  value={pointsRangeFilter.redeemedMin}
                />
                <TextField
                  data-testid="points-redeemed-max"
                  label={t($ => $.common.filter.max)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPointsRangeFilter(prev => ({
                      ...prev,
                      redeemedMax: e.target.value,
                    }))
                  }
                  type="number"
                  value={pointsRangeFilter.redeemedMax}
                />
              </Stack>
            </Stack>

            <Stack spacing={2}>
              <Text category="title-medium">{t($ => $.bookings.filters.pointsReserved)}</Text>
              <Stack direction="row" spacing={2}>
                <TextField
                  data-testid="points-reserved-min"
                  label={t($ => $.common.filter.min)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPointsRangeFilter(prev => ({
                      ...prev,
                      reservedMin: e.target.value,
                    }))
                  }
                  type="number"
                  value={pointsRangeFilter.reservedMin}
                />
                <TextField
                  data-testid="points-reserved-max"
                  label={t($ => $.common.filter.max)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPointsRangeFilter(prev => ({
                      ...prev,
                      reservedMax: e.target.value,
                    }))
                  }
                  type="number"
                  value={pointsRangeFilter.reservedMax}
                />
              </Stack>
            </Stack>

            <Stack alignItems="flex-end">
              <Button
                onClick={() => {
                  setPointsRangeFilter({
                    remainingMin: '',
                    remainingMax: '',
                    redeemedMin: '',
                    redeemedMax: '',
                    reservedMin: '',
                    reservedMax: '',
                  });
                }}
                testID="clear-points-range"
                variant="text"
              >
                {t($ => $.common.actions.clearAll)}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </Container>
  );
};

export default Bookings;
